import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit, 
  startAfter,
  Timestamp,
  increment
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject, getMetadata, uploadBytesResumable, UploadTaskSnapshot } from 'firebase/storage';
import { db, storage } from '../firebase';
import { 
  StorageFile, 
  StorageQuota, 
  StorageUsage, 
  UploadProgress, 
  StorageSettings,
  StorageFolder
} from '../types/storage';

// Storage limits by plan
const STORAGE_LIMITS = {
  free: {
    maxStorage: 100 * 1024 * 1024, // 100MB
    maxFiles: 50,
    maxFileSize: 5 * 1024 * 1024, // 5MB
    allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'application/pdf']
  },
  basic: {
    maxStorage: 1024 * 1024 * 1024, // 1GB
    maxFiles: 500,
    maxFileSize: 25 * 1024 * 1024, // 25MB
    allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf', 'text/plain']
  },
  pro: {
    maxStorage: 10 * 1024 * 1024 * 1024, // 10GB
    maxFiles: 5000,
    maxFileSize: 100 * 1024 * 1024, // 100MB
    allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml', 'application/pdf', 'text/plain', 'video/mp4', 'audio/mp3']
  },
  enterprise: {
    maxStorage: 100 * 1024 * 1024 * 1024, // 100GB
    maxFiles: 50000,
    maxFileSize: 500 * 1024 * 1024, // 500MB
    allowedTypes: ['*'] // All types allowed
  }
};

class StorageService {
  private getFileType(mimeType: string): StorageFile['type'] {
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/') || mimeType.startsWith('audio/')) return 'media';
    if (mimeType.includes('template') || mimeType.includes('html')) return 'template';
    if (mimeType.includes('svg') || mimeType.includes('ai') || mimeType.includes('psd')) return 'graphic';
    return 'file';
  }

  private formatFileSize(bytes: number): string {
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  }

  async initializeUserQuota(userId: string, workspaceId?: string | null, plan: string = 'free'): Promise<StorageQuota> {
    const quotaData: Omit<StorageQuota, 'id'> = {
      userId,
      workspaceId: workspaceId || null,
      totalStorage: STORAGE_LIMITS[plan as keyof typeof STORAGE_LIMITS].maxStorage,
      usedStorage: 0,
      fileCount: 0,
      maxFiles: STORAGE_LIMITS[plan as keyof typeof STORAGE_LIMITS].maxFiles,
      plan: plan as StorageQuota['plan'],
      limits: STORAGE_LIMITS[plan as keyof typeof STORAGE_LIMITS],
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    const docRef = await addDoc(collection(db, 'storageQuotas'), quotaData);
    return { id: docRef.id, ...quotaData };
  }

  async getUserQuota(userId: string, workspaceId?: string): Promise<StorageQuota | null> {
    const q = query(
      collection(db, 'storageQuotas'),
      where('userId', '==', userId),
      where('workspaceId', '==', workspaceId || null)
    );
    
    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) {
      return null;
    }

    const doc = querySnapshot.docs[0];
    return { id: doc.id, ...doc.data() } as StorageQuota;
  }

  async updateUserQuota(userId: string, workspaceId: string | undefined, plan: string): Promise<void> {
    const quota = await this.getUserQuota(userId, workspaceId);
    if (!quota) {
      await this.initializeUserQuota(userId, workspaceId, plan);
      return;
    }

    const newLimits = STORAGE_LIMITS[plan as keyof typeof STORAGE_LIMITS];
    await updateDoc(doc(db, 'storageQuotas', quota.id!), {
      plan: plan as StorageQuota['plan'],
      totalStorage: newLimits.maxStorage,
      maxFiles: newLimits.maxFiles,
      limits: newLimits,
      updatedAt: Timestamp.now()
    });
  }

  async checkStorageLimit(userId: string, workspaceId: string | undefined, fileSize: number): Promise<{ allowed: boolean; reason?: string }> {
    let quota = await this.getUserQuota(userId, workspaceId);

    if (!quota) {
      try {
        quota = await this.initializeUserQuota(userId, workspaceId, 'free');
      } catch (error) {
        console.warn('Failed to initialize storage quota, allowing upload but quota tracking may be broken:', error);
        // If we can't initialize quota, we should probably let the user upload anyway
        // to avoid blocking them, especially since this might be a permissions issue
        // on the storageQuotas collection.
        return { allowed: true };
      }
    }

    // Check file size limit
    if (fileSize > quota.limits.maxFileSize) {
      return { 
        allowed: false, 
        reason: `File size (${this.formatFileSize(fileSize)}) exceeds limit (${this.formatFileSize(quota.limits.maxFileSize)})` 
      };
    }

    // Check total storage limit
    if (quota.usedStorage + fileSize > quota.totalStorage) {
      return { 
        allowed: false, 
        reason: `Upload would exceed storage limit (${this.formatFileSize(quota.totalStorage)})` 
      };
    }

    // Check file count limit
    if (quota.fileCount >= quota.maxFiles) {
      return { 
        allowed: false, 
        reason: `Maximum file count (${quota.maxFiles}) reached` 
      };
    }

    return { allowed: true };
  }

  async uploadFile(
    file: File, 
    userId: string, 
    workspaceId: string | undefined,
    tags: string[] = [],
    description?: string,
    onProgress?: (progress: UploadProgress) => void,
    skipQuota: boolean = false
  ): Promise<StorageFile> {
    try {
      // Check storage limits (skip if requested, e.g. for admins)
      if (!skipQuota) {
        const limitCheck = await this.checkStorageLimit(userId, workspaceId, file.size);
        if (!limitCheck.allowed) {
          throw new Error(limitCheck.reason);
        }
      }

      // Generate unique filename
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop();
      const fileName = `${timestamp}_${Math.random().toString(36).substring(2)}.${fileExtension}`;
      const filePath = `users/${userId}/files/${fileName}`;

      console.log('Uploading file to path:', filePath);

      // Upload to Firebase Storage with progress tracking
      const storageRef = ref(storage, filePath);
      const uploadTask = uploadBytesResumable(storageRef, file);
      
      // Track upload progress
      if (onProgress) {
        onProgress({
          fileId: '',
          fileName: file.name,
          progress: 0,
          status: 'uploading'
        });
      }

      // Wrap upload task in a Promise
      const snapshot = await new Promise<UploadTaskSnapshot>((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            console.log('Upload progress:', Math.round(progress) + '%');
            if (onProgress) {
              onProgress({
                fileId: '',
                fileName: file.name,
                progress: Math.round(progress),
                status: 'uploading'
              });
            }
          },
          (error) => {
            console.error('Upload error in progress callback:', error);
            if (onProgress) {
              onProgress({
                fileId: '',
                fileName: file.name,
                progress: 0,
                status: 'error',
                error: error.message
              });
            }
            reject(error);
          },
          () => {
            // Upload completed successfully
            resolve(uploadTask.snapshot);
          }
        );
      });
      console.log('Upload task completed, getting download URL...');
      const downloadURL = await getDownloadURL(snapshot.ref);
      console.log('Download URL obtained:', downloadURL);
      
      // Get file metadata
      const metadata = await getMetadata(snapshot.ref);
      
      const fileData: Omit<StorageFile, 'id'> = {
        name: fileName,
        originalName: file.name,
        type: this.getFileType(file.type),
        mimeType: file.type,
        size: file.size,
        url: downloadURL,
        userId,
        workspaceId: workspaceId ?? null,
        tags,
        ...(description !== undefined ? { description } : {}),
        metadata: {
          format: fileExtension,
          ...(file.type.startsWith('image/') && {
            width: 0,
            height: 0
          })
        },
        isTrashed: false,
        trashedAt: null,
        isPublic: false,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      console.log('Creating file record in Firestore...');
      const docRef = await addDoc(collection(db, 'storageFiles'), fileData);
      const storageFile = { id: docRef.id, ...fileData };

      // Update quota
      await this.updateQuotaAfterUpload(userId, workspaceId, file.size);

      if (onProgress) {
        onProgress({
          fileId: docRef.id,
          fileName: file.name,
          progress: 100,
          status: 'completed'
        });
      }

      console.log('File upload completed successfully:', storageFile);
      return storageFile;
    } catch (error) {
      console.error('Error in uploadFile:', error);
      throw error;
    }
  }


  private async updateQuotaAfterUpload(userId: string, workspaceId: string | undefined, fileSize: number): Promise<void> {
    const quota = await this.getUserQuota(userId, workspaceId);
    if (!quota) return;

    await updateDoc(doc(db, 'storageQuotas', quota.id!), {
      usedStorage: increment(fileSize),
      fileCount: increment(1),
      updatedAt: Timestamp.now()
    });
  }

  private async updateQuotaAfterDelete(userId: string, workspaceId: string | undefined, fileSize: number): Promise<void> {
    const quota = await this.getUserQuota(userId, workspaceId);
    if (!quota) return;

    await updateDoc(doc(db, 'storageQuotas', quota.id!), {
      usedStorage: increment(-fileSize),
      fileCount: increment(-1),
      updatedAt: Timestamp.now()
    });
  }

  async getFiles(
    userId: string, 
    workspaceId: string | undefined,
    type?: StorageFile['type'],
    limitCount: number = 50
  ): Promise<StorageFile[]> {
    const constraints = [
      where('userId', '==', userId),
      where('workspaceId', '==', workspaceId || null)
    ];

    if (type) {
      constraints.push(where('type', '==', type));
    }

    const querySnapshot = await getDocs(query(collection(db, 'storageFiles'), ...constraints, limit(limitCount)));
    const files = querySnapshot.docs.map(doc => {
      const data = doc.data() as StorageFile;
      return {
        id: doc.id,
        ...data,
        isTrashed: data.isTrashed ?? false,
        trashedAt: data.trashedAt ?? null
      };
    });

    return files
      .filter(file => !file.isTrashed)
      .sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() ?? 0;
        const bTime = b.createdAt?.toMillis?.() ?? 0;
        return bTime - aTime;
      });
  }

  async getTrashedFiles(
    userId: string,
    workspaceId: string | undefined,
    type?: StorageFile['type'],
    limitCount: number = 50
  ): Promise<StorageFile[]> {
    const constraints = [
      where('userId', '==', userId),
      where('workspaceId', '==', workspaceId || null)
    ];

    if (type) {
      constraints.push(where('type', '==', type));
    }

    const querySnapshot = await getDocs(query(collection(db, 'storageFiles'), ...constraints, limit(limitCount)));
    const files = querySnapshot.docs.map(doc => {
      const data = doc.data() as StorageFile;
      return {
        id: doc.id,
        ...data,
        isTrashed: data.isTrashed ?? false,
        trashedAt: data.trashedAt ?? null
      };
    });

    return files
      .filter(file => file.isTrashed)
      .sort((a, b) => {
        const aTime = a.trashedAt?.toMillis?.() ?? a.createdAt?.toMillis?.() ?? 0;
        const bTime = b.trashedAt?.toMillis?.() ?? b.createdAt?.toMillis?.() ?? 0;
        return bTime - aTime;
      });
  }

  async trashFile(
    fileId: string,
    userId: string,
    workspaceId: string | undefined
  ): Promise<void> {
    const file = await this.getFile(fileId);
    if (!file || file.userId !== userId) {
      throw new Error('File not found or access denied');
    }

    await updateDoc(doc(db, 'storageFiles', fileId), {
      isTrashed: true,
      trashedAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
  }

  async restoreFile(
    fileId: string,
    userId: string,
    workspaceId: string | undefined
  ): Promise<void> {
    const file = await this.getFile(fileId);
    if (!file || file.userId !== userId) {
      throw new Error('File not found or access denied');
    }

    await updateDoc(doc(db, 'storageFiles', fileId), {
      isTrashed: false,
      trashedAt: null,
      updatedAt: Timestamp.now()
    });
  }

  async permanentlyDeleteFile(
    fileId: string,
    userId: string,
    workspaceId: string | undefined
  ): Promise<void> {
    const file = await this.getFile(fileId);
    if (!file || file.userId !== userId) {
      throw new Error('File not found or access denied');
    }

    // Delete from Firebase Storage
    const storageRef = ref(storage, file.url);
    await deleteObject(storageRef);

    // Delete from Firestore
    await deleteDoc(doc(db, 'storageFiles', fileId));

    // Update quota
    await this.updateQuotaAfterDelete(userId, workspaceId, file.size);
  }

  async getFile(fileId: string): Promise<StorageFile | null> {
    const docRef = doc(db, 'storageFiles', fileId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as StorageFile;
    }
    return null;
  }

  async deleteFile(fileId: string, userId: string, workspaceId: string | undefined): Promise<void> {
    await this.permanentlyDeleteFile(fileId, userId, workspaceId);
  }

  async updateFile(
    fileId: string, 
    userId: string, 
    updates: Partial<Pick<StorageFile, 'name' | 'tags' | 'description' | 'isPublic'>>
  ): Promise<void> {
    const file = await this.getFile(fileId);
    if (!file || file.userId !== userId) {
      throw new Error('File not found or access denied');
    }

    await updateDoc(doc(db, 'storageFiles', fileId), {
      ...updates,
      updatedAt: Timestamp.now()
    });
  }

  async getStorageUsage(userId: string, workspaceId: string | undefined): Promise<StorageUsage> {
    const quota = await this.getUserQuota(userId, workspaceId);
    if (!quota) {
      throw new Error('No storage quota found');
    }

    const files = await this.getFiles(userId, workspaceId, undefined, 1000);
    
    const usage = {
      totalFiles: files.length,
      totalSize: files.reduce((sum, file) => sum + file.size, 0),
      byType: {
        image: { count: 0, size: 0 },
        template: { count: 0, size: 0 },
        file: { count: 0, size: 0 },
        graphic: { count: 0, size: 0 },
        media: { count: 0, size: 0 },
        logo: { count: 0, size: 0 }
      },
      quota
    };

    // Calculate usage by type
    files.forEach(file => {
      usage.byType[file.type].count++;
      usage.byType[file.type].size += file.size;
    });

    return usage;
  }

  async searchFiles(
    userId: string, 
    workspaceId: string | undefined,
    searchTerm: string,
    type?: StorageFile['type']
  ): Promise<StorageFile[]> {
    const files = await this.getFiles(userId, workspaceId, type, 1000);
    
    return files.filter(file => 
      file.originalName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      file.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (file.description && file.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }

  async getFilesByTags(
    userId: string, 
    workspaceId: string | undefined,
    tags: string[]
  ): Promise<StorageFile[]> {
    const files = await this.getFiles(userId, workspaceId, undefined, 1000);
    
    return files.filter(file => 
      tags.some(tag => file.tags.includes(tag))
    );
  }

  async bulkDeleteFiles(fileIds: string[], userId: string, workspaceId: string | undefined): Promise<void> {
    for (const fileId of fileIds) {
      await this.permanentlyDeleteFile(fileId, userId, workspaceId);
    }
  }

  // Specialized function for email editor image uploads
  async uploadEditorImage(
    file: File, 
    userId: string, 
    workspaceId: string | undefined,
    onProgress?: (progress: number) => void,
    skipQuota: boolean = false
  ): Promise<{ url: string; id: string }> {
    // Check storage limits (skip if requested, e.g. for admins)
    if (!skipQuota) {
      const limitCheck = await this.checkStorageLimit(userId, workspaceId, file.size);
      if (!limitCheck.allowed) {
        throw new Error(limitCheck.reason);
      }
    }

    // Generate unique filename for editor images
    const timestamp = Date.now();
    const fileExtension = file.name.split('.').pop();
    const fileName = `editor_${timestamp}_${Math.random().toString(36).substring(2)}.${fileExtension}`;
    const filePath = `users/${userId}/editor/${fileName}`;

    // Upload to Firebase Storage
    const storageRef = ref(storage, filePath);
    const uploadTask = uploadBytes(storageRef, file);
    
    // Track upload progress
    if (onProgress) {
      onProgress(0);
    }

    const snapshot = await uploadTask;
    const downloadURL = await getDownloadURL(snapshot.ref);

    // Create file record
    const fileData: Omit<StorageFile, 'id'> = {
      name: fileName,
      originalName: file.name,
      type: 'image',
      mimeType: file.type,
      size: file.size,
      url: downloadURL,
      userId,
      workspaceId: workspaceId ?? null,
      tags: ['editor', 'email'],
      description: 'Email editor image',
      metadata: {
        format: fileExtension,
        source: 'email-editor'
      },
        isTrashed: false,
        trashedAt: null,
      isPublic: false,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    const docRef = await addDoc(collection(db, 'storageFiles'), fileData);

    // Update quota
    await this.updateQuotaAfterUpload(userId, workspaceId, file.size);

    if (onProgress) {
      onProgress(100);
    }

    return { url: downloadURL, id: docRef.id };
  }

  // Folder management methods
  async createFolder(
    name: string,
    userId: string,
    workspaceId?: string,
    parentFolderId?: string | null
  ): Promise<StorageFolder> {
    const folderData: Omit<StorageFolder, 'id'> = {
      name,
      userId,
      workspaceId: workspaceId ?? null,
      parentFolderId: parentFolderId ?? null,
      isTrashed: false,
      trashedAt: null,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    const docRef = await addDoc(collection(db, 'storageFolders'), folderData);
    return { id: docRef.id, ...folderData };
  }

  async getFolders(
    userId: string,
    workspaceId?: string,
    parentFolderId?: string | null
  ): Promise<StorageFolder[]> {
    const q = query(
      collection(db, 'storageFolders'),
      where('userId', '==', userId)
    );

    const querySnapshot = await getDocs(q);
    let folders = querySnapshot.docs.map(doc => {
      const data = doc.data() as StorageFolder;
      return {
        id: doc.id,
        ...data,
        isTrashed: data.isTrashed ?? false,
        trashedAt: data.trashedAt ?? null
      };
    });

    const targetWorkspaceId = workspaceId ?? null;
    folders = folders.filter(folder => (folder.workspaceId ?? null) === targetWorkspaceId);

    if (parentFolderId !== undefined) {
      const targetParent = parentFolderId ?? null;
      folders = folders.filter(folder => (folder.parentFolderId ?? null) === targetParent);
    }

    folders = folders.filter(folder => !folder.isTrashed);

    folders.sort((a, b) => {
      const aTime = a.createdAt?.toMillis?.() ?? 0;
      const bTime = b.createdAt?.toMillis?.() ?? 0;
      return bTime - aTime;
    });

    return folders;
  }

  async getTrashedFolders(
    userId: string,
    workspaceId?: string,
    parentFolderId?: string | null
  ): Promise<StorageFolder[]> {
    const q = query(
      collection(db, 'storageFolders'),
      where('userId', '==', userId)
    );

    const querySnapshot = await getDocs(q);
    let folders = querySnapshot.docs.map(doc => {
      const data = doc.data() as StorageFolder;
      return {
        id: doc.id,
        ...data,
        isTrashed: data.isTrashed ?? false,
        trashedAt: data.trashedAt ?? null
      };
    });

    const targetWorkspaceId = workspaceId ?? null;
    folders = folders.filter(folder => (folder.workspaceId ?? null) === targetWorkspaceId);

    if (parentFolderId !== undefined) {
      const targetParent = parentFolderId ?? null;
      folders = folders.filter(folder => (folder.parentFolderId ?? null) === targetParent);
    }

    folders = folders.filter(folder => folder.isTrashed);

    folders.sort((a, b) => {
      const aTime = a.trashedAt?.toMillis?.() ?? a.createdAt?.toMillis?.() ?? 0;
      const bTime = b.trashedAt?.toMillis?.() ?? b.createdAt?.toMillis?.() ?? 0;
      return bTime - aTime;
    });

    return folders;
  }

  async trashFolder(
    folderId: string,
    userId: string,
    workspaceId: string | undefined
  ): Promise<void> {
    const folder = await this.getFolder(folderId);
    if (!folder || folder.userId !== userId) {
      throw new Error('Folder not found or access denied');
    }

    await updateDoc(doc(db, 'storageFolders', folderId), {
      isTrashed: true,
      trashedAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
  }

  async restoreFolder(
    folderId: string,
    userId: string,
    workspaceId: string | undefined
  ): Promise<void> {
    const folder = await this.getFolder(folderId);
    if (!folder || folder.userId !== userId) {
      throw new Error('Folder not found or access denied');
    }

    await updateDoc(doc(db, 'storageFolders', folderId), {
      isTrashed: false,
      trashedAt: null,
      updatedAt: Timestamp.now()
    });
  }

  async permanentlyDeleteFolder(
    folderId: string,
    userId: string,
    workspaceId: string | undefined
  ): Promise<void> {
    const folder = await this.getFolder(folderId);
    if (!folder || folder.userId !== userId) {
      throw new Error('Folder not found or access denied');
    }

    // Ensure no files remain associated with the folder
    const files = await this.getFiles(userId, workspaceId, undefined, 1000);
    const filesInFolder = files.filter(file => file.folderId === folderId);

    for (const file of filesInFolder) {
      await this.permanentlyDeleteFile(file.id!, userId, workspaceId);
    }

    await deleteDoc(doc(db, 'storageFolders', folderId));
  }

  async deleteFolder(folderId: string, userId: string, workspaceId?: string): Promise<void> {
    const folder = await this.getFolder(folderId);
    if (!folder || folder.userId !== userId) {
      throw new Error('Folder not found or access denied');
    }

    // Check if folder has files
    const files = await this.getFiles(userId, workspaceId);
    const filesInFolder = files.filter(f => f.folderId === folderId);
    if (filesInFolder.length > 0) {
      throw new Error('Cannot delete folder with files. Please move or delete files first.');
    }

    // Check for subfolders
    const subfolders = await this.getFolders(userId, workspaceId, folderId);
    if (subfolders.length > 0) {
      throw new Error('Cannot delete folder with subfolders. Please delete subfolders first.');
    }

    await deleteDoc(doc(db, 'storageFolders', folderId));
  }

  async getFolder(folderId: string): Promise<StorageFolder | null> {
    const docRef = doc(db, 'storageFolders', folderId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as StorageFolder;
    }
    return null;
  }

  async updateFolder(
    folderId: string,
    userId: string,
    updates: Partial<Pick<StorageFolder, 'name' | 'color'>>
  ): Promise<void> {
    const folder = await this.getFolder(folderId);
    if (!folder || folder.userId !== userId) {
      throw new Error('Folder not found or access denied');
    }

    await updateDoc(doc(db, 'storageFolders', folderId), {
      ...updates,
      updatedAt: Timestamp.now()
    });
  }

  async moveFileToFolder(fileId: string, userId: string, folderId: string | null): Promise<void> {
    const file = await this.getFile(fileId);
    if (!file || file.userId !== userId) {
      throw new Error('File not found or access denied');
    }

    await updateDoc(doc(db, 'storageFiles', fileId), {
      folderId: folderId || null,
      updatedAt: Timestamp.now()
    });
  }
}

export const storageService = new StorageService();

// Export the uploadEditorImage function for backward compatibility
export const uploadEditorImage = (file: File, userId: string, workspaceId: string | undefined, onProgress?: (progress: number) => void) => {
  return storageService.uploadEditorImage(file, userId, workspaceId, onProgress);
};